import _ from 'lodash';
import {Fragment, useContext, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Select from 'react-select';
import {Role} from "../../types/Role";
import {User} from "../../types/User";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {AuthUtility} from '../common/AuthUtility';
import {CheckBox} from "../common/CheckBox";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {SelectOption} from "../common/CommonUIElementTypes";
import {DelayedSearchBar} from "../common/DelayedSearchBar";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PhoneUtility} from "../common/PhoneUtility";
import {SelectUtility} from "../common/SelectUtility";
import {UrlUtility} from "../common/UrlUtility";
import {ListPaginationOptions} from '../common/ListPaginationOptions';
import {PillButton} from "../common/PillButton";

interface UsersListPageURLQueryParams {
    sortDirection?: string;
    page?: number;
    roleId?: any;
    searchText?: string;
    showInactive?: any;
}

export const UsersListPage = function () {
    if (!AuthUtility.userHasEnforcedPermissions([Constants.permissionNames.userCreate])) return null;
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.users.pathSegment}`;
    const dataStore = useRenderableDataStore({
        usersList: [],
        loading: true,
        sortDirection: Constants.sortDirections.asc,
        pageCount: 0,
        selectedPage: 1,
        filterRoleId: null as any,
        searchText: '',
        showInactive: false,
        roleOptions: [] as SelectOption[],

        prevUrlQueryString: null,
        prevParams: null,
    })
    const location = useLocation();
    const routeParams = useParams();

    // Update state if URL changes.
    if (
        (location && location.search) !== dataStore.data().prevUrlQueryString ||
        routeParams !== dataStore.data().prevParams
    ) {
        let selectedPage = 1;
        let sortDirection = Constants.sortDirections.asc;
        let filterRoleId: any = null;
        let searchText = '';
        let showInactive = false;

        let queryParams: UsersListPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);
        if (queryParams.sortDirection) {
            sortDirection = queryParams.sortDirection;
        }

        if (queryParams.page) {
            selectedPage = queryParams.page;
        }

        if (queryParams.roleId) {
            filterRoleId = queryParams.roleId;
        }

        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        if (queryParams.showInactive) {
            showInactive = queryParams.showInactive === 'true' || queryParams.showInactive === true;
        }

        dataStore.updateAndRender({
            sortDirection: sortDirection,
            selectedPage: selectedPage,
            filterRoleId: filterRoleId,
            searchText: searchText,
            showInactive: showInactive,
            prevUrlQueryString: location.search,
            prevParams: routeParams,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadListDebounced();
        },
        [
            '' + dataStore.data().sortDirection,
            '' + dataStore.data().selectedPage,
            '' + dataStore.data().filterRoleId,
            '' + dataStore.data().searchText,
            '' + dataStore.data().showInactive
        ]
    );

    const syncDebounced = _.debounce(() => {
        dataStore.updateAndRender({loading: true})
        ApiUtility.syncUsers()
            .then(function (syncUsersReport: any) {
                if (syncUsersReport && syncUsersReport.error) {
                    dataStore.updateAndRender({loading: false});
                    MessageDisplayerUtility.error(syncUsersReport.error);
                    return;
                }
                const failures = (syncUsersReport && syncUsersReport.failures) || [];
                if (failures.length > 0) {
                    const failureLines = failures
                        .map((failure: {external_acumatica_id: string; display_name: string}) =>
                            `${failure.external_acumatica_id} -- ${failure.display_name}`)
                        .join('\n');
                    MessageDisplayerUtility.error(
                        `User sync completed with ${failures.length} failure${failures.length === 1 ? '' : 's'}:\n${failureLines}`
                    );
                } else {
                    MessageDisplayerUtility.success('User sync completed successfully.')
                }
                reloadListDebounced();
            })
            .catch(function (error: any) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while syncing users.`);
            });
    }, Constants.debounceTimeoutMs);

    const reloadListDebounced = _.debounce(() => {
        let dataStoreUpdate: any = {};
        dataStore.updateAndRender({loading: true})

        ApiUtility.getUsersList(
            dataStore.data().selectedPage,
            dataStore.data().sortDirection,
            dataStore.data().filterRoleId,
            dataStore.data().searchText,
            dataStore.data().showInactive
        )
            .then(function (listData: any) {
                dataStoreUpdate = {
                    usersList: listData.list,
                    pageCount: listData.pageCount,
                    loading: false
                };

                // Fetch data for filter dropdowns one time (if option lists are empty).
                return new Promise<void>(function (resolve, reject) {
                    if (dataStore.data().roleOptions.length === 0) {
                        return ApiUtility.getRolesList()
                            .then(function (rolesList: Role[]) {
                                dataStoreUpdate.roleOptions = SelectUtility.modelArrayToSelectOptions(
                                    rolesList, 'id', 'name'
                                )
                                resolve()
                            })
                            .catch(reject)
                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                dataStore.updateAndRender(dataStoreUpdate);
            })
            .catch(function (error) {
                dataStore.updateAndRender({...dataStoreUpdate, loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the users list page.`);
            });
    }, Constants.debounceTimeoutMs);

    function handleSortChange(option: SelectOption) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            sortDirection: option.value,
            page: 1,
        };
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleRoleChange(option: SelectOption | null) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            page: 1,
        };
        updatedQueryParams.roleId = option?.value || null;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleShowInactiveChange(checked: boolean) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            page: 1,
        };
        updatedQueryParams.showInactive = checked || undefined;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleSearchChange(updatedSearchText: string) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            page: 1,
        };
        updatedQueryParams.searchText = updatedSearchText || undefined;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function goToAddUserPage() {
        context.navigator.navigateTo(`${urlBase}/add${location.search}`);
    }

    function goToUpdateUserPage(user: User) {
        context.navigator.navigateTo(`${urlBase}/${user.id}`);
    }

    function renderUsersListDisplay(usersList: User[]) {
        let usersListDisplay = [];

        for (let i = 0; i < usersList.length; i++) {
            const user = usersList[i];
            const userStatusRowClass = !user.user_invite_sent
                ? ' not-invited'
                : !user.user_invite_accepted
                    ? ' pending-invite'
                    : !user.active
                        ? ' deactivated'
                        : '';
            const inactiveSuffix = !user.user_invite_accepted || user.active ? '' : ' (Inactive)';
            const showSyncWarningIndicator = user.subsequent_auto_sync_failures >= 3 && !!user.acumatica_id;

            let userActionIconSource = '/static/images/login-setup-icon.svg';
            if (!user.user_invite_sent) {
                userActionIconSource = '/static/images/paper-airplane-textcolor.svg';
            } else if (user.user_invite_accepted) {
                userActionIconSource = '/static/images/edit-icon-textcolor.svg';
            }

            usersListDisplay.push(
                <div
                    key={i + 1}
                    className={`common-list-row users-list-row${userStatusRowClass}`}
                    onClick={() => goToUpdateUserPage(user)}
                >
                    <div className="common-list-row-content">
                        <div className="common-list-row-ticket-edge">
                            <div className="common-list-row-ticket-circle"></div>
                        </div>
                        <div className="common-list-row-main">
                            {user.person.name}{inactiveSuffix}
                            <ConditionalRenderer
                                if={showSyncWarningIndicator}
                                renderer={() => (
                                    <img
                                        src="/static/images/warning-triangle-red.svg"
                                        className="common-list-row-sync-warning-indicator"
                                        title="Automatic sync is disabled for this user due to repeated errors. Successfully manually syncing this user will re-enable auto sync."
                                    />
                                )}
                            />
                        </div>
                        <div className="common-list-row-details">
                            <div className="common-list-row-detail-cell role">
                                <img
                                    className="common-list-row-detail-icon"
                                    src="/static/images/users-icon-slate.svg"
                                />
                                <div className="common-list-row-detail-text">
                                    {user.user_roles[0].role.name}
                                </div>
                            </div>
                            <ConditionalRenderer
                                if={user.user_invite_accepted}
                                renderer={() => (
                                    <div className="common-list-row-detail-cell phone">
                                        <img
                                            className="common-list-row-detail-icon"
                                            src="/static/images/phone-slate.svg"
                                        />
                                        <div className="common-list-row-detail-text">
                                            {PhoneUtility.dbPhoneToUsersListDisplayPhone('' + user.person.phone) || 'N/A'}
                                        </div>
                                    </div>
                                )}
                            />
                            <ConditionalRenderer
                                if={user.user_invite_accepted}
                                renderer={() => (
                                    <div className="common-list-row-detail-cell email">
                                        <img
                                            className="common-list-row-detail-icon"
                                            src="/static/images/email-slate.svg"
                                        />
                                        <div className="common-list-row-detail-text">
                                            {user.person.email}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                        <div className="common-list-row-action-indicators">
                            <div className="common-list-row-action-cell">
                                <img
                                    src={userActionIconSource}
                                    className="user-edit-action-icon"
                                />
                            </div>
                            <div className="common-list-row-pointer-cell">
                                <img
                                    src="/static/images/arrow-right-tiny-green.svg"
                                    className="common-list-row-pointer-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return usersListDisplay;
    }

    if (dataStore.data().usersList) {
        return (
            <div className="common-list-page users-list-page">
                <div className="common-list-page-filters-header">
                    <div className="common-list-page-filters-header-content">
                        <div className="common-list-page-filters-header-content-left">
                            <div className="common-list-sort-control-container">
                                <Select
                                    className="common-list-sort-control"
                                    classNamePrefix="common-list-sort-control"
                                    options={Constants.listSortOptions}
                                    value={SelectUtility.selectOptionForId(
                                        dataStore.data().sortDirection,
                                        Constants.listSortOptions
                                    )}
                                    onChange={(option) => handleSortChange(option as SelectOption)}
                                    components={{
                                        Control: (controlProps: any) => (
                                            <div
                                                {...(controlProps.innerProps || {})}
                                                className="sort-filter-control"
                                            >
                                                <img
                                                    className="common-list-page-filter-icon sort-filter-icon"
                                                    src={
                                                        dataStore.data().sortDirection === Constants.sortDirections.asc
                                                            ? "/static/images/sort-ascending-textcolor.svg"
                                                            : "/static/images/sort-descending-textcolor.svg"
                                                    }
                                                />
                                                <img
                                                    className="common-list-page-filter-caret sort-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                                <div className="sort-filter-control-hidden-children">
                                                    {controlProps.children}
                                                </div>
                                            </div>
                                        )
                                    }}
                                />
                            </div>
                            <div className="common-list-page-select-filter-container role-filter-container">
                                <img
                                    className="common-list-page-filter-icon role-filter-icon"
                                    src="/static/images/users-icon-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().roleOptions}
                                    value={SelectUtility.selectOptionForId(
                                        dataStore.data().filterRoleId,
                                        dataStore.data().roleOptions
                                    )}
                                    placeholder="Role"
                                    isClearable={true}
                                    onChange={(option) => handleRoleChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            <div className="show-inactive-filter-container">
                                <CheckBox
                                    id="users-list-show-inactive-checkbox"
                                    checked={dataStore.data().showInactive}
                                    onChange={(checked: boolean) => handleShowInactiveChange(checked)}
                                    label="show inactive users"
                                />
                            </div>
                        </div>
                        <div className="common-list-page-filters-header-content-right">
                            <DelayedSearchBar
                                value={dataStore.data().searchText}
                                performSearch={handleSearchChange}
                                placeholder="Search users"
                            />
                            <ConditionalRenderer
                                if={AuthUtility.userHasOneOfThesePermissions([Constants.permissionNames.userUpdate])}
                                renderer={() => (
                                    <PillButton
                                        onClick={syncDebounced}
                                        buttonText="SYNC"
                                        buttonClasses="sync-button black-border-button"
                                        containerClasses="sync-button-container"
                                        rightIconSrc="/static/images/refresh-textcolor.svg"
                                        disabled={dataStore.data().loading}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
                <div className="common-list-page-header">
                    <div className="common-list-page-header-content">
                        <div className="common-list-page-header-content-left">
                        </div>
                        <div className="common-list-page-header-content-right">
                            <div
                                className="icon-button add-button"
                                onClick={goToAddUserPage}
                            >
                                <div className="icon-button-content">
                                    <img
                                        className="icon-button-image"
                                        src="/static/images/plus-green.svg"
                                    />
                                    <div className="icon-button-text">Add User</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={dataStore.data().loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (dataStore.data().usersList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-content users-list-content">
                                        {renderUsersListDisplay(dataStore.data().usersList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={dataStore.data().pageCount}
                                        selectedPage={dataStore.data().selectedPage}
                                        urlBase={urlBase}
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No users found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
